import React, { useState } from 'react';
import { Save, ShieldCheck, User, KeyRound } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { authService } from '../services/api';
import { useAuthStore } from '../store';
import { toast } from 'react-toastify';

const Profile = () => {
  const { user, updateUser } = useAuthStore();
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    cpf: user?.cpf || '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfileData((current) => ({ ...current, [name]: value }));
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordData((current) => ({ ...current, [name]: value }));
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setProfileLoading(true);

    try {
      const response = await authService.updateProfile(profileData);
      updateUser(response.data.data);
      toast.success('Perfil atualizado com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar perfil');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    if (passwordData.newPassword.length < 8) {
      toast.error('A nova senha precisa ter pelo menos 8 caracteres');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('A confirmação da senha não confere');
      return;
    }

    setPasswordLoading(true);

    try {
      await authService.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      toast.success('Senha alterada com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao alterar senha');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-5xl">
            <div className="mb-6 flex flex-col gap-2">
              <h1 className="text-2xl font-bold text-white">Meu perfil</h1>
              <p className="text-dark-400">
                Atualize seus dados e mantenha o acesso administrativo protegido.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <section className="card">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500/20 text-primary-400">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Dados da conta</h2>
                    <p className="text-sm text-dark-400">{user?.email}</p>
                  </div>
                </div>

                <form onSubmit={handleProfileSubmit} className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-dark-300">
                      Nome
                    </label>
                    <input
                      name="name"
                      value={profileData.name}
                      onChange={handleProfileChange}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-dark-300">
                      Telefone
                    </label>
                    <input
                      name="phone"
                      value={profileData.phone}
                      onChange={handleProfileChange}
                      className="input-field"
                      placeholder="(00) 00000-0000"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-dark-300">
                      CPF
                    </label>
                    <input
                      name="cpf"
                      value={profileData.cpf}
                      onChange={handleProfileChange}
                      className="input-field"
                      placeholder="000.000.000-00"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={profileLoading}
                    className="btn-primary flex w-full items-center justify-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {profileLoading ? 'Salvando...' : 'Salvar dados'}
                  </button>
                </form>
              </section>

              <section className="card">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-500/20 text-accent-400">
                    <KeyRound className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Alterar senha</h2>
                    <p className="text-sm text-dark-400">Use uma senha forte e exclusiva.</p>
                  </div>
                </div>

                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-dark-300">
                      Senha atual
                    </label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-dark-300">
                      Nova senha
                    </label>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      className="input-field"
                      minLength={8}
                      required
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-dark-300">
                      Confirmar nova senha
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      className="input-field"
                      minLength={8}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="btn-primary flex w-full items-center justify-center gap-2"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    {passwordLoading ? 'Alterando...' : 'Alterar senha'}
                  </button>
                </form>
              </section>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Profile;
